import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { ServiceModal } from '@/components/ServiceModal';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import { useFilteredVehicles } from '@/contexts/AppDataContext';
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  Mail,
  Instagram,
  Facebook,
  Youtube,
  Linkedin,
  Twitter
} from 'lucide-react';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import weappsLogo from '@/assets/weapps-logo.png';
import mobiautoIcon from '@/assets/mobiauto_icon.png';
import { getStoreBrandAssets } from '@/lib/storeBrand';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import { StoreFooterIcon } from '@/components/StoreFooterIcon';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';

interface FooterProps {
  config: any;
}

export const Footer = ({ config }: FooterProps) => {
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const { colors } = useTheme();
  const primaryColor = colors.primary;
  const [selectedService, setSelectedService] = useState<any>(null);
  const vehicles = useFilteredVehicles(selectedStore);
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  // Format opening hours from config
  const formatOpeningHours = () => {
    const hours = store.openingHours?.default;
    if (!hours) return [];

    const daysMap: { [key: string]: string } = {
      'sunday': 'Domingo',
      'monday': 'Segunda-feira',
      'tuesday': 'Terça-feira',
      'wednesday': 'Quarta-feira',
      'thursday': 'Quinta-feira',
      'friday': 'Sexta-feira',
      'saturday': 'Sábado'
    };

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const formattedHours: { day: string; time: string }[] = [];

    // Check if weekdays have the same hours
    const weekdayHours = weekDays.map(day => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      formattedHours.push({
        day: 'Segunda a Sexta',
        time: `${hours.monday.from} - ${hours.monday.to}`
      });
    } else {
      // Add each weekday separately if they differ
      weekDays.forEach(day => {
        if (hours[day]?.from && hours[day]?.to) {
          formattedHours.push({
            day: daysMap[day],
            time: `${hours[day].from} - ${hours[day].to}`
          });
        }
      });
    }

    // Add Saturday
    if (hours.saturday?.from && hours.saturday?.to) {
      formattedHours.push({
        day: 'Sábado',
        time: `${hours.saturday.from} - ${hours.saturday.to}`
      });
    } else if (!hours.saturday?.from) {
      formattedHours.push({
        day: 'Sábado',
        time: 'Fechado'
      });
    }

    // Add Sunday
    if (hours.sunday?.from && hours.sunday?.to) {
      formattedHours.push({
        day: 'Domingo',
        time: `${hours.sunday.from} - ${hours.sunday.to}`
      });
    } else if (!hours.sunday?.from) {
      formattedHours.push({
        day: 'Domingo',
        time: 'Fechado'
      });
    }

    return formattedHours;
  };

  const openingHours = formatOpeningHours();

  // Check if vehicles exist for each category
  const hasSeminovos = vehicles.some(v => !v.isZeroKm && v.mileage > 0 && !v.armored);
  const hasZeroKm = vehicles.some(v => v.isZeroKm || v.mileage === 0);
  const hasBlindados = vehicles.some(v => v.armored);

  // Count how many categories we have
  const categoryCount = [hasSeminovos, hasZeroKm, hasBlindados].filter(Boolean).length;

  const quickLinks = [
    { label: 'Início', href: '/' },
    { label: 'Estoque', href: '/estoque' },
  ];

  // Add conditional menu items only if we have 2 or more categories
  if (categoryCount >= 2) {
    if (hasSeminovos) {
      quickLinks.push({ label: 'Seminovos', href: '/estoque?type=seminovo' });
    }
    if (hasZeroKm) {
      quickLinks.push({ label: 'Zero KM', href: '/estoque?type=zerokm' });
    }
    if (hasBlindados) {
      quickLinks.push({ label: 'Blindados', href: '/estoque?armored=true' });
    }
  }

  // Add other links
  quickLinks.push(
    { label: 'Favoritos', href: '/favoritos' },
    { label: 'Contato', href: '/contato' }
  );

  // Get services dynamically from config
  const getServiceName = (serviceId: string) => {
    const serviceNames: Record<string, string> = {
      '65da42048eca108d87c91da6': 'Agendar Videochamada',
      '65dcb7548eca108d87c91dbb': 'Avaliação',
      '65dcb7c58eca108d87c91dbd': 'Consignação',
      '65dcef468eca108d87c91dc8': 'Consórcio',
      '65dcb6528eca108d87c91db9': 'Despachante',
      '65dcb7898eca108d87c91dbc': 'Financiamento',
      '6628fd535bda2ae51f6fc5ff': 'Peças e Acessórios',
      '65dcef098eca108d87c91dc7': 'Revisões',
      '65dcb7238eca108d87c91dba': 'Seguros',
      '65dceeac8eca108d87c91dc6': 'Serviços',
      '65dcb7fe8eca108d87c91dbe': 'Tabela FIPE',
      '65dcb5e78eca108d87c91db8': 'Test-Drive',
      '65dcb8548eca108d87c91dbf': 'Vendas Diretas'
    };

    return serviceNames[serviceId] || 'Serviço';
  };

  const getServiceImage = (serviceId: string) => {
    const serviceImages: Record<string, string> = {
      '65da42048eca108d87c91da6': videoCall,
      '65dcb7548eca108d87c91dbb': valuation,
      '65dcb7c58eca108d87c91dbd': consignment,
      '65dcef468eca108d87c91dc8': consortium,
      '65dcb6528eca108d87c91db9': forwardingAgent,
      '65dcb7898eca108d87c91dbc': financing,
      '6628fd535bda2ae51f6fc5ff': partsAccessories,
      '65dcef098eca108d87c91dc7': scheduleReview,
      '65dcb7238eca108d87c91dba': insurance,
      '65dceeac8eca108d87c91dc6': scheduleServices,
      '65dcb7fe8eca108d87c91dbe': fipeTable,
      '65dcb5e78eca108d87c91db8': testdrive,
      '65dcb8548eca108d87c91dbf': directSales
    };

    return serviceImages[serviceId] || '';
  };

  const servicesList = store.services || [];

  const { hasLogo } = getStoreBrandAssets(config, store);

  return (
    <footer
      className="text-white border-t-4"
      style={{ backgroundColor: primaryColor, borderTopColor: primaryColor }}
    >
      {/* Top Section - Company Info & Social */}
      <div className="border-b border-primary-foreground/10">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">

            {/* Company Logo & Name */}
            <div className="flex items-center gap-4">
              <StoreFooterIcon
                config={config}
                store={store}
                alt={config.name}
                wrapperClassName="flex items-center justify-center shrink-0"
              />
              {!hasLogo && (
                <div>
                  <h3 className="text-2xl font-bold">{config.name}</h3>
                  {config.slogan && (
                    <p className="text-primary-foreground/70 text-sm mt-1">{config.slogan}</p>
                  )}
                </div>
              )}
            </div>

            {/* Social Networks - All Platforms */}
            {config.socialNetworks && (
              <div className="flex gap-3 flex-wrap">
                {config.socialNetworks.instagram && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.instagram, '_blank')}
                    aria-label="Abrir Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.facebook && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.facebook, '_blank')}
                    aria-label="Abrir Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.youtube && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.youtube, '_blank')}
                    aria-label="Abrir YouTube"
                  >
                    <Youtube className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.linkedin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.linkedin, '_blank')}
                    aria-label="Abrir LinkedIn"
                  >
                    <Linkedin className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.twitter && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.twitter, '_blank')}
                    aria-label="Abrir Twitter"
                  >
                    <Twitter className="h-5 w-5" />
                  </Button>
                )}
                {config.socialNetworks.tiktok && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20 hover:text-white rounded-full h-12 w-12 transition-all hover:scale-110"
                    onClick={() => window.open(config.socialNetworks.tiktok, '_blank')}
                    aria-label="Abrir TikTok"
                  >
                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                    </svg>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Middle Section - Info Grid */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          

          {/* Navigation & Services Combined */}
          <div className="grid grid-cols-2 gap-8">
            {/* Navigation */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Navegação</h4>
              <ul className="space-y-2.5">
                {quickLinks.slice(0, 7).map((link, index) => (
                <li key={index}>
                  {link.href.startsWith('#') ? (
                    <a
                      href={link.href}
                      className="text-primary-foreground/80 hover:text-white transition-all text-sm hover:translate-x-1 inline-block"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-primary-foreground/80 hover:text-white transition-all text-sm hover:translate-x-1 inline-block"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-bold mb-6 text-white">Serviços</h4>
              <ul className="space-y-2.5">
                {servicesList.map((service: any, index: number) => (
                <li key={index}>
                  <button
                    onClick={() => setSelectedService(service)}
                    className="text-white/85 hover:text-white transition-all text-sm text-left hover:translate-x-1 inline-block"
                  >
                    {getServiceName(service.service)}
                  </button>
                </li>
              ))}
            </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Contato</h4>
            <div className="space-y-4">
              <button
                onClick={() => {
                  const address = store?.place?.address || 'Endereço da empresa';
                  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
                  window.open(mapsUrl, '_blank');
                }}
                className="flex items-start gap-3 text-white/90 hover:text-white transition-colors group text-left w-full"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-white/85 mb-0.5">Endereço</p>
                  <p className="text-sm font-medium leading-relaxed">
                    {store?.place?.address || 'Endereço da empresa'}
                  </p>
                </div>
              </button>
              <a
                href={store.phone?.ios}
                className="flex items-start gap-3 text-white/90 hover:text-white transition-colors group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-white/85 mb-0.5">Telefone</p>
                  <span className="text-sm font-medium">{store.phone?.formatted}</span>
                </div>
              </a>
              <button
                onClick={() => openWhatsApp(store.whatsapp?.ios)}
                className="flex items-start gap-3 text-white/90 hover:text-white transition-colors group text-left w-full"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-white/85 mb-0.5">WhatsApp</p>
                  <span className="text-sm font-medium">{store.whatsapp?.formatted}</span>
                </div>
              </button>
              <a
                href={`mailto:${config.email}`}
                className="flex items-start gap-3 text-white/90 hover:text-white transition-colors group"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-xs text-white/85 mb-0.5">E-mail</p>
                  <span className="text-sm font-medium">{config.email}</span>
                </div>
              </a>
            </div>
          </div>
          {/* Opening Hours & Apps */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white">Horário de Atendimento</h4>
            <div className="space-y-3 mb-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="space-y-1.5">
                  {openingHours.map((hour, index) => (
                  <div key={index} className="text-sm text-white/85">
                      <span className="font-medium text-white">{hour.day}:</span> {hour.time}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* App Download Buttons */}
            {(hasGooglePlayLink || hasAppStoreLink) && (
              <div>
                <p className="text-sm font-semibold text-white mb-3 text-center md:text-left">Baixe nosso app</p>
                <div className="flex gap-2 justify-center md:justify-start">
                  {hasGooglePlayLink && (
                    <a
                      href={config.googlePlay.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={googlePlay}
                        alt="Google Play"
                        width={135}
                        height={40}
                      />
                    </a>
                  )}
                  {hasAppStoreLink && (
                    <a
                      href={appStoreUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={appStore}
                        alt="App Store"
                        width={135}
                        height={40}
                      />
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-primary-foreground/10">
        <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/80">
            <p>&copy; {new Date().getFullYear()} {config.name}. Todos os direitos reservados.</p>

            <div className="flex items-center gap-3">
              <Link
                to="/politica-privacidade"
                className="text-white/80 hover:text-white transition-colors"
              >
                Política de Privacidade
              </Link>
              <span>•</span>
              <Link
                to="/termos-uso"
                className="text-white/80 hover:text-white transition-colors"
              >
                Termos de Uso
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Service Modal */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          serviceTitle={getServiceName(selectedService.service)}
          config={config}
          isOpen={!!selectedService}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedService(null);
            }
          }}
          serviceImage={selectedService.image || getServiceImage(selectedService.service)}
        />
      )}
    </footer>
  );
};